import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import PublicRoutes from './PublicRoutes';
import PrivateRoutes from './PrivateRoutes';
import { useAuthStore } from '@/stores/auth';
import { useAlertStore } from '@/stores/alert';
import usePermissionsComposable from '@/composables/permissions/checkPermissions';
import { usePermitsStore } from '@/stores/user/permissions/permissions';
import { useAgentStore } from '@/stores/agent';

const MAINTENANCE_MODE = false;

/**
 * Carga dinámica de rutas personalizadas desde brand/ si existe
 */
async function loadBrandRoutes() {
    // Usar import.meta.glob con ruta relativa
    // Desde core/src/router/index.ts busca en ../../../brand/src/router/index.ts
    const modules = import.meta.glob('../../../brand/src/router/index.ts');
    
    console.log('[ROUTER] 🔍 Checking for brand routes...', Object.keys(modules));
    
    if (Object.keys(modules).length === 0) {
        console.log('[ROUTER] ℹ️  No brand routes found, using only core routes');
        return null;
    }
    
    try {
        const moduleLoader = modules[Object.keys(modules)[0]];
        console.log('[ROUTER] ✅ Found brand router, loading...');
        
        const brandModule = await moduleLoader() as any;
        
        return {
            privateRoutes: brandModule.brandPrivateRoutes || [],
            publicRoutes: brandModule.brandPublicRoutes || [],
            moduleRoutes: brandModule.brandModuleRoutes || {}
        };
    } catch (error: any) {
        console.warn('[ROUTER] ⚠️  Error loading brand routes:', error?.message || error);
        return null;
    }
}

/**
 * Merge inteligente de rutas: brand puede sobrescribir o agregar
 */
function mergeRoutes(
    coreRoutes: RouteRecordRaw[],
    brandRoutes: RouteRecordRaw[]
): RouteRecordRaw[] {
    if (!brandRoutes || brandRoutes.length === 0) {
        return coreRoutes;
    }

    const coreRoutesMap = new Map<string, RouteRecordRaw>();
    coreRoutes.forEach((route) => {
        if (route.name) {
            coreRoutesMap.set(String(route.name), route);
        }
    });

    const mergedRoutes = [...coreRoutes];

    brandRoutes.forEach((brandRoute) => {
        if (brandRoute.name && coreRoutesMap.has(String(brandRoute.name))) {
            // Sobrescribir ruta existente
            const index = mergedRoutes.findIndex((r) => r.name === brandRoute.name);
            if (index !== -1) {
                mergedRoutes[index] = brandRoute;
                console.log(`[ROUTER] 🔄 Overriding route: ${String(brandRoute.name)}`);
            }
        } else {
            // Agregar nueva ruta
            mergedRoutes.push(brandRoute);
            console.log(`[ROUTER] ➕ Adding new route: ${String(brandRoute.name) || "unnamed"}`);
        }
    });

    return mergedRoutes;
}

// Cargar rutas de brand de forma asíncrona
const brandRoutes = await loadBrandRoutes();

// Preparar rutas finales
let finalPublicRoutes = PublicRoutes;
let finalPrivateRoutes = PrivateRoutes;

if (brandRoutes) {
    console.log('[ROUTER] 🎨 Merging brand routes with core routes...');
    
    // Mergear rutas públicas
    if (brandRoutes.publicRoutes.length > 0) {
        const publicChildren = PublicRoutes.children || [];
        const mergedPublicChildren = mergeRoutes(publicChildren, brandRoutes.publicRoutes);
        finalPublicRoutes = {
            ...PublicRoutes,
            children: mergedPublicChildren,
        };
        console.log(`[ROUTER] ✅ Merged ${brandRoutes.publicRoutes.length} public brand route(s)`);
    }

    // Mergear rutas privadas
    if (brandRoutes.privateRoutes.length > 0) {
        const privateChildren = PrivateRoutes.children || [];
        const mergedPrivateChildren = mergeRoutes(privateChildren, brandRoutes.privateRoutes);
        finalPrivateRoutes = {
            ...PrivateRoutes,
            children: mergedPrivateChildren,
        };
        console.log(`[ROUTER] ✅ Merged ${brandRoutes.privateRoutes.length} private brand route(s)`);
    }
} else {
    console.log('[ROUTER] 🔧 Using core routes only (standalone mode)');
}

export const router = createRouter({
    history: createWebHistory(),
    routes: [finalPublicRoutes, finalPrivateRoutes]
});

router.beforeEach(async (to, _from, next) => {
    if (MAINTENANCE_MODE && to.name !== 'Maintenance') {
        return next({ name: 'Maintenance', replace: true });
    }

    // redirect to login page if not logged in and trying to access a restricted page
    const publicPages = ['/auth/login'];
    const authRequired = !publicPages.includes(to.path);
    const auth: any = useAuthStore();
    const storePermits: any = usePermitsStore();
    const agentStore = useAgentStore();

    //if (to.path === '/auth/login' && auth.user) {
    //    return next('/private'); // Ajusta '/private' a tu ruta de dashboard real
    //}
    if (to.name === 'Login' && auth.user) {
        const hashFolio = to.params.hash_folio as string | undefined;
        if (!hashFolio) {
            return next('/private'); // ruta por defecto
        } else {
            return next(`/private/activity/detail/v2/${hashFolio}`); // usa el valor real
        }
    }

    if (to.matched.some((record) => record.meta.requiresAuth)) {
        if (authRequired && !auth.user) {
            auth.returnUrl = to.fullPath;
            auth.returnName = to.name;
            return next('/auth/login');
        }

        if (auth.user) {
            try {
                await storePermits.getPermits();
            } catch (e) {
                console.error('[ROUTER] Error loading permits:', e);
            }
        }

        try {
            await agentStore.getInfoAgentGet();
        } catch (e) {
            console.error('[ROUTER] Error loading agent info:', e);
        }

        const userStore = useAuthStore();

        if (to.meta.disabled) {
            return next({ name: 'Mi Tablero' });
        }

        if (to.meta.requiresSystemPermission) {
            // Validar que requiresSystemPermission sea string o string[]
            const requiredPermission = to.meta.requiresSystemPermission;
            if (typeof requiredPermission !== 'string' && !Array.isArray(requiredPermission)) {
                return next();
            }
            
            // Usar permissionsUser del store o de localStorage como fallback
            const permissionsToCheck = storePermits.permissionsUser && storePermits.permissionsUser.length > 0 
                ? storePermits.permissionsUser 
                : (() => {
                    try {
                        const stored = localStorage.getItem('permissionsUser');
                        return stored ? JSON.parse(stored) : [];
                    } catch {
                        return [];
                    }
                })();
            
            if (usePermissionsComposable(requiredPermission, permissionsToCheck, agentStore.isAgent)) {
                return next(); // Permite el acceso
            } else {
                const alertStore = useAlertStore();
                alertStore.addAlert({
                    message: 'No tienes permisos para esta sección.',
                    type: 'error',
                    duration: 3000
                });

                return next({ name: 'NoPermissions' });
            }
        } else if (to.meta.requiresPermission) {
            // Si la ruta requiere permisos
            const requiredPermissions = Array.isArray(to.meta.requiresPermission)
                ? to.meta.requiresPermission
                : [to.meta.requiresPermission];

            if (userStore.groups != null) {
                // Verificar si el usuario pertenece a al menos uno de los grupos requeridos
                const hasPermissionGroup = userStore.groups.some((group: { name: string }) => requiredPermissions.includes(group.name));

                /*   const hasPermissionSystem = userStore.groups.some((group: { name: string }) => requiredPermissions.includes(group.name));
                 ; */


                if (hasPermissionGroup) {
                    return next(); // Permite el acceso
                } else {
                    const alertStore = useAlertStore();
                    alertStore.addAlert({
                        message: 'No tienes permisos para esta sección.',
                        type: 'error',
                        duration: 3000
                    });

                    return next({ name: 'NoPermissions' });
                }
            }
        }
    }

    next(); // Asegura que next se llame en todas las rutas
});
